import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { saveSession } from "@/lib/session";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /auth/callback — the redirect target Emergent Google Auth lands on.
 * It receives `#session_id=...` in the URL hash. We exchange it for a
 * Crowther session_token via `/api/auth/google/callback`, then bounce
 * the user to their `return_to` (or /gateway).
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const [status, setStatus] = useState("processing"); // 'processing' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    const returnParam = new URLSearchParams(loc.search).get("return_to");
    const returnTo =
      returnParam && returnParam.startsWith("/") ? returnParam : "/gateway-2";

    if (!sessionId) {
      setStatus("error");
      setErrorMsg("Sign-in did not complete. Please try again.");
      return;
    }

    (async () => {
      try {
        const res = await axios.post(
          `${API}/api/auth/google/callback`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        if (res.data?.ok) {
          saveSession(res.data.session_token);
          try {
            window.history.replaceState(null, "", loc.pathname);
          } catch {}
          nav(returnTo, { replace: true });
        } else {
          setStatus("error");
          setErrorMsg("Sign-in did not complete. Please try again.");
        }
      } catch (err) {
        setStatus("error");
        setErrorMsg(
          err?.response?.data?.detail ||
            "Google sign-in failed. Please try again."
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="auth-callback" style={wrap}>
      {status === "processing" ? (
        <div style={box}>
          <div style={spinner} />
          <p style={text}>Completing sign-in…</p>
        </div>
      ) : (
        <div style={box}>
          <p style={{ ...text, color: "#F6C6B7" }} data-testid="auth-callback-error">
            {errorMsg}
          </p>
          <button
            type="button"
            onClick={() => nav("/login-2", { replace: true })}
            style={btn}
            data-testid="auth-callback-retry"
          >
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
}

const wrap = {
  minHeight: "100vh",
  background: "#161E1B",
  color: "#F6F1EA",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--font-sans)",
};
const box = {
  display: "flex", flexDirection: "column", alignItems: "center", gap: 20,
};
const text = { fontSize: 14, letterSpacing: "0.02em", margin: 0 };
const spinner = {
  width: 28, height: 28,
  border: "2px solid rgba(246,241,234,0.2)",
  borderTopColor: "#A1AC9C",
  borderRadius: "50%",
  animation: "cw-spin 900ms linear infinite",
};
const btn = {
  padding: "12px 24px",
  fontSize: 13, letterSpacing: "0.04em",
  background: "#A1AC9C", color: "#161E1B",
  border: 0, borderRadius: 999, cursor: "pointer",
};

// inject keyframes once
if (typeof document !== "undefined" && !document.getElementById("cw-auth-spin")) {
  const s = document.createElement("style");
  s.id = "cw-auth-spin";
  s.textContent = "@keyframes cw-spin { to { transform: rotate(360deg) } }";
  document.head.appendChild(s);
}
